import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  gap?: number;
  className?: string;
}

export function Tooltip({
  children,
  content,
  className = '',
  gap = 8,
}: TooltipProps): JSX.Element {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback((): void => {
    if (triggerRef.current && tooltipRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current.offsetHeight;
      setPosition({
        top: rect.top - tooltipHeight - gap,
        left: rect.left + rect.width / 2,
      });
    }
  }, [gap]);

  const handleMouseEnter = (): void => {
    setIsVisible(true);
    // Small delay to ensure tooltip is rendered before measuring
    setTimeout(updatePosition, 0);
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [isVisible, updatePosition]);

  return (
    <div
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`fixed px-4 py-2 text-sm bg-white rounded-md shadow-lg border border-gray-200 after:content-[''] after:absolute after:left-1/2 after:-bottom-2 after:-translate-x-1/2 after:border-8 after:border-transparent after:border-t-white after:border-l-white after:rotate-45 after:bg-white after:w-4 after:h-4 ${className}`}
            style={{
              zIndex: 999999,
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform: 'translate(-50%, 0)',
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </div>
  );
}
